'use client';

import { Controller, FormProvider } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form';
import { Button, SelectDropdown, Input, TextArea, CheckBox, Typography } from '@/app/common';
import { Label } from '@/app/common/Label';
import type { ContactFormData } from '@workspace/interfaces/contact-form.types';
import type { ContactFormFieldConfig, ContactFormFieldsProps } from '../interfaces';
import {
  FORM_FIELD_KINDS,
  INPUT_TYPES,
  contactFormFieldsConfig,
  CONTACT_FORM_MAX_LENGTH_DEFAULT,
  INPUT_MAX_LENGTH,
} from '@workspace/constants/contact-form.constant';
import { formatPhoneAU, isValidPhoneAU, trimToMaxLengthAU } from '@/app/lib/helper';
import { getDropdownOptions } from '../lib/contact-form.utils';

export function ContactFormFields({
  form,
  loading,
  onMessageChange,
  onSubmit,
  children,
  fields,
  showForm = true,
}: ContactFormFieldsProps) {
  const fallbackFields: ReadonlyArray<ContactFormFieldConfig> =
    fields ?? (contactFormFieldsConfig as ReadonlyArray<ContactFormFieldConfig>);
  const maxLengthDefault = CONTACT_FORM_MAX_LENGTH_DEFAULT;

  if (!showForm) {
    return <div className="flex-1 font-openSans max-w-none">{children}</div>;
  }

  return (
    <div className="flex-1 font-openSans max-w-none">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-xl">
          <Controller
            control={form.control}
            name="honeypot"
            render={({ field }) => (
              <input type="text" tabIndex={-1} autoComplete="off" style={{ display: 'none' }} {...field} />
            )}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-lg gap-x-md">
            {fallbackFields.map((f) => {
              const isEnquiryType = f.name === 'enquiry_type___dropdown';
              const col = f.fullWidth || isEnquiryType ? 'lg:col-span-2' : '';

              switch (f.kind) {
                case FORM_FIELD_KINDS.INPUT:
                  return (
                    <Controller
                      key={String(f.name)}
                      control={form.control}
                      name={f.name as unknown as FieldPath<ContactFormData>}
                      rules={
                        f.name === 'phone'
                          ? {
                              validate: (v) => isValidPhoneAU(String(v || '')) || 'Invalid phone number.',
                            }
                          : undefined
                      }
                      render={({ field, fieldState }) => {
                        const handleBlur = () => {
                          field.onBlur();
                          form.trigger(f.name as unknown as FieldPath<ContactFormData>);
                        };

                        return (
                          <div className={`w-full flex-1 ${col} ${f.className ?? ''}`}>
                            <Label
                              text={f.label}
                              isRequired={!!f.required}
                              className="text-base text-secondary-400 font-semibold leading-6 mb-xs block"
                            />
                            {f.name === 'phone' ? (
                              <Input
                                type="tel"
                                placeholder={f.placeholder ?? 'Enter your phone number'}
                                value={formatPhoneAU((field.value as string) ?? '')}
                                onChange={(val: string) => {
                                  field.onChange(trimToMaxLengthAU(val));
                                }}
                                onBlur={handleBlur}
                                status={fieldState.error ? 'error' : 'default'}
                                className="w-full max-w-none h-11 py-xs px-button border rounded-sm bg-white text-gray-900 focus-within:border-secondary-200 focus-within:shadow-focus"
                                inputClassName="placeholder:text-secondary-200!"
                                variant="field"
                              />
                            ) : (
                              <Input
                                type={f.type ?? INPUT_TYPES.TEXT}
                                placeholder={f.placeholder ?? ''}
                                value={(field.value as string) ?? ''}
                                onChange={field.onChange}
                                onBlur={handleBlur}
                                maxLength={
                                  (INPUT_MAX_LENGTH as Partial<Record<keyof ContactFormData, number>>)[
                                    f.name as keyof ContactFormData
                                  ]
                                }
                                status={fieldState.error ? 'error' : 'default'}
                                className="w-full max-w-none h-11 py-xs px-button border rounded-sm bg-white text-gray-900 focus-within:border-secondary-200 focus-within:shadow-focus"
                                inputClassName="placeholder:text-secondary-200!"
                                variant="field"
                              />
                            )}
                            {fieldState.error && (
                              <Typography variant="bodySm" className="mt-xs !text-xs !text-error">
                                {fieldState.error.message}
                              </Typography>
                            )}
                          </div>
                        );
                      }}
                    />
                  );
                case FORM_FIELD_KINDS.DROPDOWN: {
                  const opts = f.options ?? getDropdownOptions(f.name);
                  return (
                    <Controller
                      key={String(f.name)}
                      control={form.control}
                      name={f.name as unknown as FieldPath<ContactFormData>}
                      render={({ field, fieldState }) => (
                        <div
                          className={`${isEnquiryType ? 'w-full space-y-xs' : 'w-full flex-1'} ${col} ${f.className ?? ''}`}
                        >
                          <Label
                            text={f.label}
                            isRequired={!!f.required}
                            className="text-base text-secondary-400 font-semibold leading-6 mb-xs block"
                          />
                          <SelectDropdown
                            label=""
                            placeholder={f.placeholder ?? ''}
                            options={opts}
                            value={(field.value as string) ?? ''}
                            onChange={(val) => {
                              field.onChange(val);
                              form.trigger(f.name as unknown as FieldPath<ContactFormData>);
                            }}
                            onBlur={() => {
                              field.onBlur();
                              form.trigger(f.name as unknown as FieldPath<ContactFormData>);
                            }}
                            className="w-full"
                            status={fieldState.error ? 'error' : 'default'}
                          />
                          {fieldState.error && (
                            <Typography variant="bodySm" className="mt-xs !text-xs !text-error">
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </div>
                      )}
                    />
                  );
                }
                case FORM_FIELD_KINDS.TEXTAREA: {
                  const maxLen = f.maxLength ?? maxLengthDefault;
                  return (
                    <Controller
                      key={String(f.name)}
                      control={form.control}
                      name={f.name as unknown as FieldPath<ContactFormData>}
                      render={({ field, fieldState }) => {
                        const value = (field.value as string) ?? '';
                        return (
                          <div className={`w-full space-y-xs ${col} ${f.className ?? ''}`}>
                            <Label
                              text={f.label}
                              isRequired={!!f.required}
                              className="text-base font-semibold leading-6 mb-xs block"
                            />
                            <TextArea
                              id={String(f.name)}
                              placeholder={f.placeholder ?? ''}
                              value={value}
                              onChange={(newValue) => {
                                const safeValue = newValue.slice(0, maxLen);
                                field.onChange(safeValue);
                                onMessageChange(safeValue);
                              }}
                              onBlur={() => {
                                field.onBlur();
                                form.trigger(f.name as unknown as FieldPath<ContactFormData>);
                              }}
                              status={fieldState.error ? 'error' : 'default'}
                              errorMessage={fieldState.error?.message}
                            />
                          </div>
                        );
                      }}
                    />
                  );
                }
                case FORM_FIELD_KINDS.CHECKBOX:
                default:
                  return (
                    <Controller
                      key={String(f.name)}
                      control={form.control}
                      name={f.name as unknown as FieldPath<ContactFormData>}
                      render={({ field }) => (
                        <div className={`space-y-xs ${col} ${f.className ?? ''}`}>
                          <CheckBox
                            variant="default"
                            isChecked={!!field.value}
                            onChange={field.onChange}
                            iconName="Tick"
                            boxClassName="w-5 h-5"
                            iconSize={16}
                          >
                            <span className="text-sm font-body text-foreground text-secondary-500">{f.label}</span>
                          </CheckBox>
                          {f.description && (
                            <Typography variant="bodyXs" className="text-secondary-500">
                              {f.description}
                            </Typography>
                          )}
                        </div>
                      )}
                    />
                  );
              }
            })}
          </div>

          <div className="space-y-sm">
            {children}

            {/* Submit Button */}
            <Button
              type="submit"
              isDisabled={loading}
              variant="primary"
              className="flex w-full md:w-60 md:min-w-5xl h-11 min-h-11 max-h-11 px-sm py-button items-center gap-xs rounded-sm"
            >
              <span className="block w-full text-center text-white font-sora text-base font-semibold leading-5">
                {loading ? 'Sending...' : 'Submit'}
              </span>
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
